import { useMutation } from '@tanstack/react-query'
import { IHttpError, IResponse, Model, ObjectsFactory } from './core'
import { IFile } from './file'

export enum BalanceStatusEnum {
  Pending = 'pending',
  Unpaid = 'unpaid',
  Paid = 'paid',
  Partial = 'partial'
}

export function useBalanceStatusOptions() {
  const { __ } = useTranslator()

  return [
    { value: BalanceStatusEnum.Pending, label: __('Pending') },
    { value: BalanceStatusEnum.Unpaid, label: __('Unpaid') },
    { value: BalanceStatusEnum.Paid, label: __('Paid') },
    { value: BalanceStatusEnum.Partial, label: __('Partial') }
  ]
}

export interface IBalance {
  message: string
  data: BalanceRecord[]
  meta: Meta
}

export interface BalanceRecord {
  id: number
  created_at: string
  updated_at: string
  month: string
  company_id: number
  contract_id: number
  request_num: number
  request_amount: string
  service_fee: string
  voucher_discount: string
  eligibility_amount: string
  vat_rate: string
  total_paid: string
  status: string
  extra: any
  cumulative_debt_data: string
  company: BalanceCompany
  contract: BalanceContract
  cus_pay_diff: number
  lift_paid: number
}

export interface BalanceCompany {
  id: number
  created_at: string
  updated_at: string
  created_by: number
  updated_by: number
  name: string
  system_code: string
  company_code: string
  business_number: any
  address: any
  phone: any
  email: any
  bank_id: any
  bank_account_number: any
  establish_date: any
  company_type: any
  poc_firstname: any
  poc_phone: any
  poc_phone_code: any
  total_user: number
  working_rate: string
  working_type: string
  service_fee: string
  vat_rate: string
  setting_updated_at: string
  setting_updated_by: string
  logo_id?: string
  status: string
  logo_file?: IFile
}

export interface BalanceContract {
  id: number
  created_at: string
  updated_at: string
  created_by: number
  updated_by: number
  company_id: number
  file_id: string
  start_date: string
  end_date: string
  status: string
}

export interface Meta {
  total: number
  perPage: number
}

export interface BalanceStatistics {
  voucher_total: string
  fee_tax_total: string
  service_fee_total: string
  service_fee_tax_total: string
  monthly_growth: {
    voucher_growth: string
    fee_tax_growth: string
    service_fee_growth: string
    service_fee_tax_growth: string
  }
}

export interface IBalancePartnerAmountItem {
  status: boolean
  amount: number
  reason: string
}

export type IBalancePartnerAmountMap = Record<string, IBalancePartnerAmountItem>

export interface IBalanceAmountResponse {
  message: string
  data: IBalancePartnerAmountMap
}

const modelConfig = {
  path: '/balance'
}

export class Balance extends Model {
  static queryKeys = {
    paginate: 'BALANCE_PAGINATE_QUERY',
    findOne: 'BALANCE_FIND_ONE_QUERY',
    getBalanceStatistics: 'BALANCE_GET_BALANCE_STATISTICS_QUERY',
    getBalanceMount: 'BALANCE_GET_BALANCE_AMOUNT_QUERY',
    getChartTotalTransactionFees: 'BALANCE_GET_CHART_TOTAL_TRANSACTION_FEES_QUERY',
    getChartTotalEwaDisbursement: 'BALANCE_GET_CHART_TOTAL_EWA_DISBURSEMENT_QUERY'
  }

  static objects = ObjectsFactory.factory<IBalance>(modelConfig, this.queryKeys)

  static getBalanceStatistics(params: any) {
    return {
      queryKey: [this.queryKeys.getBalanceStatistics, params],
      queryFn: (): Promise<IBalance> => {
        return this.api
          .get<IBalance>({
            url: '/balance/companies',
            params
          })
          .then(r => r.data)
      }
    }
  }

  static getBalanceAmount() {
    return {
      queryKey: [this.queryKeys.getBalanceMount],
      queryFn: (): Promise<IBalanceAmountResponse> => {
        return this.api
          .get<IBalanceAmountResponse>({
            url: '/balance/balance'
          })
          .then(r => r.data)
      }
    }
  }

  static getChartTotalTransactionFees(params: any) {
    return {
      queryKey: [this.queryKeys.getChartTotalTransactionFees, params],
      queryFn: (): Promise<any> => {
        return this.api
          .get<any>({
            url: '/balance/chart-total-transaction-fee',
            params
          })
          .then(r => r.data)
      }
    }
  }
  static getChartTotalEwaDisbursement(params: any) {
    return {
      queryKey: [this.queryKeys.getChartTotalEwaDisbursement, params],
      queryFn: (): Promise<any> => {
        return this.api
          .get<any>({
            url: '/balance/chart-total-ewa-disbursement',
            params
          })
          .then(r => r.data)
      }
    }
  }
  static getBalanceStatisticsCard(params: any) {
    return {
      queryKey: ['BALANCE_GET_BLANCE_STATISTICS_QUERY', params],
      queryFn: (): Promise<IBalance> => {
        return this.api
          .get<IBalance>({
            url: '/balance/balance-statistics',
            params
          })
          .then(r => r.data)
      }
    }
  }
  static getBalanceStatisticsCompare(params: any) {
    return {
      queryKey: ['BALANCE_GET_BALANCE_STATISTICS_COMPARE_QUERY', params],
      queryFn: (): Promise<IBalance> => {
        return this.api
          .get<IBalance>({
            url: '/balance/balance-statistics-compare',
            params
          })
          .then(r => r.data)
      }
    }
  }
  static getBalanceDeptByCompanyId(params: any) {
    return {
      queryKey: ['BALANCE_GET_BALANCE_DEPT_BY_COMPANY_ID_QUERY', params],
      queryFn: (): Promise<any> => {
        return this.api
          .get<any>({
            url: `/balance/balance-dept`,
            params
          })
          .then(r => r.data)
      }
    }
  }
  static getBalanceStatus(params: any) {
    return {
      queryKey: ['BALANCE_GET_BALANCE_STATUS_QUERY', params],
      queryFn: (): Promise<any> => {
        return this.api
          .get<any>({
            url: `/balance/status`,
            params
          })
          .then(r => r.data)
      }
    }
  }
}

Balance.setup(modelConfig)

export function useUpdateBalanceSettingFinanceBulkMutation() {
  return useMutation<IResponse<any>, IHttpError, { data: any }>({
    mutationFn: data => {
      return Balance.api
        .post<IResponse<any>>({
          url: `${modelConfig.path}/setting/bulk`,
          data
        })
        .then(r => r.data)
    }
  })
}

export function useUpdateBalanceSettingFinanceByCompanyIdMutation() {
  return useMutation<IResponse<any>, IHttpError, { data: any; companyId: number }>({
    mutationFn: ({ data, companyId }) => {
      return Balance.api
        .put<IResponse<any>>({
          url: `${modelConfig.path}/setting/${companyId}`,
          data
        })
        .then(r => r.data)
    }
  })
}
